import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  MdSchool,
  MdPerson,
  MdFilterList,
  MdSearch,
  MdClose,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdSave,
  MdSecurity,
  MdBuild,
  MdStar,
  MdVerifiedUser,
  MdCleaningServices,
} from "react-icons/md";
import { useTraining } from "context/TrainingContext";
import { useWorkers } from "context/WorkersContext";
import { useTrainingTopics } from "context/TrainingTopicsContext";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";

const TOPIC_ICONS_MAP = {
  "text-orange-500": MdSecurity,
  "text-blue-500": MdBuild,
  "text-emerald-500": MdVerifiedUser,
  "text-purple-500": MdStar,
  "text-amber-500": MdCleaningServices,
  "text-red-500": MdSecurity,
  "text-cyan-500": MdVerifiedUser,
};

function statusSummary(tr, topics) {
  const s = tr.topicStatuses || {};
  const ids = topics.map((t) => t.id);
  let completed = 0;
  let notCompleted = 0;
  ids.forEach((id) => {
    if (s[id] === "completed") completed++;
    else if (s[id] === "not_completed") notCompleted++;
  });
  return { completed, notCompleted, total: ids.length };
}

function ViewDetailsModal({ tr, workers, topics, onClose }) {
  const supervisor = workers.find((w) => w.id === tr.supervisorId);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="view-training-title"
      >
        <div className="shrink-0 flex items-center justify-between gap-4 p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800">
          <h2 id="view-training-title" className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
            Training details
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Operator</p>
              <p className="font-bold text-slate-900 dark:text-white">{tr.operatorName || "—"}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Sector</p>
              <p className="font-bold text-slate-900 dark:text-white">{tr.sectorName || "—"}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Supervisor</p>
              <p className="font-bold text-slate-900 dark:text-white">{supervisor?.name ?? "—"}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Date</p>
              <p className="font-bold text-slate-900 dark:text-white">
                {tr.date ? new Date(tr.date).toLocaleString() : "—"}
              </p>
            </div>
          </div>
          <div>
            <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm uppercase tracking-wider mb-3">
              Training topics — status & notes
            </h3>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <th className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300">Topic</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300 w-32">Status</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic) => {
                    const iconColor = topic.iconColor || "text-slate-500";
                    const Icon = TOPIC_ICONS_MAP[iconColor] || MdStar;
                    const st = (tr.topicStatuses || {})[topic.id];
                    const notes = (tr.topicNotes || {})[topic.id];
                    return (
                      <tr key={topic.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 ${iconColor}`}>
                            <Icon className="text-base shrink-0" />
                            <span className="font-medium text-slate-900 dark:text-white">{topic.name}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${
                              st === "completed"
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            }`}
                          >
                            {st === "completed" ? "Completed" : st === "not_completed" ? "Not completed" : "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          {notes ? notes : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="shrink-0 p-4 sm:p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

function EditTrainingModal({ tr, topics, onClose, onSave }) {
  const [topicStatuses, setTopicStatuses] = useState({});
  const [topicNotes, setTopicNotes] = useState({});

  useEffect(() => {
    if (tr) {
      setTopicStatuses({ ...(tr.topicStatuses || {}) });
      setTopicNotes({ ...(tr.topicNotes || {}) });
    }
  }, [tr]);

  const handleStatusChange = (topicId, status) => {
    setTopicStatuses((prev) => ({ ...prev, [topicId]: status }));
  };

  const handleNoteChange = (topicId, text) => {
    setTopicNotes((prev) => ({ ...prev, [topicId]: text }));
  };

  const allSet = topics.every(
    (t) => topicStatuses[t.id] === "completed" || topicStatuses[t.id] === "not_completed"
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!allSet || !tr?.id) return;
    onSave(tr.id, { topicStatuses: { ...topicStatuses }, topicNotes: { ...topicNotes } });
    onClose();
  };

  if (!tr) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-training-title"
      >
        <div className="shrink-0 flex items-center justify-between gap-4 p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800">
          <h2 id="edit-training-title" className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
            Edit training record
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {tr.operatorName} · {tr.sectorName}
            </p>
            <div className="space-y-4">
              {topics.map((topic) => {
                const iconColor = topic.iconColor || "text-slate-500";
                const Icon = TOPIC_ICONS_MAP[iconColor] || MdStar;
                const status = topicStatuses[topic.id];
                return (
                  <div
                    key={topic.id}
                    className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row gap-4 items-start"
                  >
                    <div className="w-full sm:w-1/3 shrink-0">
                      <div className={`flex items-center gap-2 mb-1 ${iconColor}`}>
                        <Icon className="text-lg shrink-0" />
                        <h4 className="font-bold text-slate-900 dark:text-white text-sm">{topic.name}</h4>
                      </div>
                    </div>
                    <div className="flex-1 w-full flex flex-col gap-2">
                      <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full max-w-sm gap-1">
                        {["completed", "not_completed"].map((s) => (
                          <React.Fragment key={s}>
                            <input
                              type="radio"
                              name={`edit-status-${topic.id}`}
                              id={`edit-${topic.id}-${s}`}
                              checked={status === s}
                              onChange={() => handleStatusChange(topic.id, s)}
                              className="sr-only"
                            />
                            <label
                              htmlFor={`edit-${topic.id}-${s}`}
                              className={`flex-1 text-center py-2 text-sm font-bold rounded-lg cursor-pointer transition-all ${
                                status === s ? "text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                              }`}
                              style={status === s ? { backgroundColor: s === "completed" ? "#059669" : SECONDARY } : {}}
                            >
                              {s === "completed" ? "Completed" : "Not completed"}
                            </label>
                          </React.Fragment>
                        ))}
                      </div>
                      <textarea
                        value={topicNotes[topic.id] ?? ""}
                        onChange={(e) => handleNoteChange(topic.id, e.target.value)}
                        placeholder={`Notes for ${topic.name}…`}
                        rows={2}
                        className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none placeholder-slate-400 resize-none"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="shrink-0 p-4 sm:p-6 border-t border-slate-100 dark:border-slate-800 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!allSet}
              className="px-6 py-2.5 rounded-xl text-white font-black text-sm shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: SECONDARY, boxShadow: `${SECONDARY}33 0 10px 20px` }}
            >
              <MdSave className="text-lg" />
              Save changes
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function DeleteConfirmModal({ tr, onClose, onConfirm }) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-6"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-training-title"
      >
        <h2 id="delete-training-title" className="text-lg font-black text-slate-900 dark:text-white mb-2">
          Delete training record?
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          This will permanently remove the training record for{" "}
          <span className="font-bold text-slate-900 dark:text-white">{tr?.operatorName ?? "this operator"}</span> in{" "}
          <span className="font-bold text-slate-900 dark:text-white">{tr?.sectorName ?? "this sector"}</span>.
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (tr?.id) onConfirm(tr.id);
              onClose();
            }}
            className="px-6 py-2.5 rounded-xl font-bold text-sm text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <MdDelete className="text-lg" />
            Delete
          </button>
        </div>
      </div>
    </>
  );
}

export default function TrainingRecords() {
  const { trainings, updateTraining, removeTraining } = useTraining();
  const { workers } = useWorkers();
  const { getTopicsForSector } = useTrainingTopics();
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [viewTr, setViewTr] = useState(null);
  const [editTr, setEditTr] = useState(null);
  const [deleteTr, setDeleteTr] = useState(null);

  const sectors = useMemo(
    () => [...new Set(trainings.map((t) => t.sectorName).filter(Boolean))].sort(),
    [trainings]
  );

  // Get topics for the selected record's sector (keyed by sector name from API)
  const viewTopics = useMemo(() => {
    if (!viewTr) return [];
    return getTopicsForSector(viewTr.sectorName) || getTopicsForSector(viewTr.sectorId) || [];
  }, [viewTr, getTopicsForSector]);

  const editTopics = useMemo(() => {
    if (!editTr) return [];
    return getTopicsForSector(editTr.sectorName) || getTopicsForSector(editTr.sectorId) || [];
  }, [editTr, getTopicsForSector]);

  const filtered = useMemo(() => {
    return trainings.filter((tr) => {
      const matchSearch =
        !search.trim() ||
        (tr.operatorName && tr.operatorName.toLowerCase().includes(search.trim().toLowerCase())) ||
        (tr.sectorName && tr.sectorName.toLowerCase().includes(search.trim().toLowerCase()));
      const matchSector = !sectorFilter || tr.sectorName === sectorFilter;
      return matchSearch && matchSector;
    });
  }, [trainings, search, sectorFilter]);

  const handleEditSave = useCallback(
    (id, updates) => {
      updateTraining(id, updates);
      setEditTr(null);
    },
    [updateTraining]
  );

  const handleDeleteConfirm = useCallback(
    (id) => {
      removeTraining(id);
      setDeleteTr(null);
    },
    [removeTraining]
  );

  return (
    <div className="p-4 md:p-6 pt-4 md:pt-6 space-y-6 max-w-[1200px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-start gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
              style={{ backgroundColor: `${SECONDARY}20`, color: SECONDARY }}
            >
              Training
            </span>
          </div>
          <h2 className="text-slate-900 dark:text-white text-2xl md:text-3xl font-black tracking-tight">
            Training records
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-2xl">
            Records come from training submitted on the Conduct training page. View, edit, or delete entries below.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 min-w-0">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by operator or sector…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <MdFilterList className="text-slate-500 shrink-0" />
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm font-medium px-4 py-2.5 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[#0e76a8]/30"
          >
            <option value="">All sectors</option>
            {sectors.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <MdSchool className="text-xl" style={{ color: PRIMARY }} />
          <h3 className="font-black text-slate-900 dark:text-white">Records</h3>
        </div>
        <div className="overflow-x-auto">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <MdPerson className="mx-auto text-4xl text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-slate-500 dark:text-slate-400 font-medium">
                {trainings.length === 0
                  ? "No training records yet. Submit training from the Conduct training page."
                  : "No records match your filters."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Operator</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Sector</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Status</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Date</th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-black uppercase text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tr) => {
                  const trTopics = getTopicsForSector(tr.sectorName) || getTopicsForSector(tr.sectorId) || [];
                  const { completed, notCompleted, total } = statusSummary(tr, trTopics);
                  const summary = `${completed}/${total} completed${notCompleted ? `, ${notCompleted} not done` : ""}`;
                  return (
                    <tr
                      key={tr.id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30"
                    >
                      <td className="px-4 sm:px-6 py-3 font-medium text-slate-900 dark:text-white">{tr.operatorName}</td>
                      <td className="px-4 sm:px-6 py-3 text-slate-600 dark:text-slate-400">{tr.sectorName}</td>
                      <td className="px-4 sm:px-6 py-3">
                        <span className="text-slate-700 dark:text-slate-300 font-mono text-xs">
                          {summary}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-slate-500 dark:text-slate-400 text-xs">
                        {tr.date ? new Date(tr.date).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setViewTr(tr)}
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-[#0e76a8] transition-colors"
                            title="View details"
                            aria-label="View details"
                          >
                            <MdVisibility className="text-lg" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditTr(tr)}
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                            title="Edit"
                            aria-label="Edit"
                          >
                            <MdEdit className="text-lg" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTr(tr)}
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Delete"
                            aria-label="Delete"
                          >
                            <MdDelete className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {viewTr && <ViewDetailsModal tr={viewTr} workers={workers} topics={viewTopics} onClose={() => setViewTr(null)} />}
      {editTr && (
        <EditTrainingModal
          tr={editTr}
          topics={editTopics}
          onClose={() => setEditTr(null)}
          onSave={handleEditSave}
        />
      )}
      {deleteTr && (
        <DeleteConfirmModal
          tr={deleteTr}
          onClose={() => setDeleteTr(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
